import * as React from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { Popover, PopoverAnchor, PopoverContent } from "./popover";

// Searchable single select: type to filter, arrow keys to move, Enter to pick; allowCreate also offers the typed text.
const Combobox = ({ value, options = [], onChange, placeholder = "Select…", searchPlaceholder = "Type to search…", emptyText = "No matches.", disabled = false, allowCreate = false, createLabel = (text) => `Use "${text}"`, className }) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(0);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);
  const listId = React.useId();

  const selected = options.find((o) => o.value === value) || (allowCreate && value ? { value, label: value } : undefined);
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
    if (!allowCreate || !q || options.some((o) => o.label.toLowerCase() === q)) return matches;
    const text = query.trim().replace(/\s+/g, " ");
    return [...matches, { value: text, label: createLabel(text) }];
  }, [options, query, allowCreate, createLabel]);

  React.useEffect(() => setActive(0), [query, open]);
  React.useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView?.({ block: "nearest" });
  }, [active]);

  const pick = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setQuery("");
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[active]) pick(filtered[active]); }
    else if (e.key === "Escape") { setOpen(false); setQuery(""); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverAnchor asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span className={cn("truncate", !selected && "text-muted-foreground")}>{selected ? selected.label : placeholder}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
        onOpenAutoFocus={(e) => { e.preventDefault(); inputRef.current?.focus(); }}
      >
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={searchPlaceholder}
          className="h-9 w-full border-b border-border bg-transparent px-3 text-sm placeholder:text-muted-foreground focus:outline-none"
        />
        <div id={listId} ref={listRef} role="listbox" className="max-h-60 overflow-y-auto p-1">
          {filtered.length === 0 && <p className="px-2 py-3 text-center text-sm text-muted-foreground">{emptyText}</p>}
          {filtered.map((opt, i) => (
            <div
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              onMouseDown={(e) => { e.preventDefault(); pick(opt); }}
              onMouseEnter={() => setActive(i)}
              className={cn(
                "flex cursor-pointer items-center justify-between rounded-sm px-2 py-1.5 text-sm",
                i === active && "bg-accent text-accent-foreground"
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate">{opt.label}</span>
                {opt.tag}
              </span>
              {opt.value === value && <Check className="h-4 w-4 shrink-0" />}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { Combobox };
